<template>
  <div
    class="fixed z-50 flex justify-center items-start lg:pt-0 lg:items-center pt-[40px] w-full h-full top-0 left-0 bg-[rgba(0,0,0,0.9)] transition-all duration-300"
    :class="{
      'opacity-0 invisible': !popup.showPopup,
      'opacity-1 visible': popup.showPopup,
    }"
  >
    <div class="absolute w-full h-full left-0 top-0" @click="closePopup"></div>

    <div
      class="w-[339px] max-w-full max-h-[calc(100vh-155px)] overflow-auto bg-black p-[30px] pb-[50px] flex flex-col shadow-[0px_0px_12.5px_0px_rgba(255,255,255,0.40)] rounded-[10px] bg-center bg-cover relative"
      :style="{
        backgroundImage: 'url(' + bgImage + ')',
      }"
    >
      <button
        @click="closePopup"
        class="inline-block w-[18px] h-[18px] object-contain ml-auto transition-all duration-300 hover:opacity-70"
      >
        <img src="/times.svg" alt="back" />
      </button>
      <img
        v-if="achievementImages[type as AchievementType]"
        :src="achievementImages[type as AchievementType]?.src"
        alt="achievement"
        :class="achievementImages[type as AchievementType].class"
      />

      <h3 class="text-center text-[18px] leading-[120%] mb-[25px]">
        Achievements
      </h3>
      <span v-if="isLoading" class="loader"></span>
      <div v-else class="grid grid-cols-2 gap-[22px]">
        <div
          v-for="(achievement, index) in achievements[type]"
          :key="index"
          class="achievement-item"
        >
          <img
            :src="'/achievements/' + type + '-' + (index + 1) + '.svg'"
            alt="achievement"
          />
          <h4>{{ achievement.name }}</h4>
          <p>{{ achievement.text }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  PagedList,
  IGameAchievementItems,
  GroupedAchievements,
} from "~/types";

const user = useUserStore();
const popup = usePopupStore();
const { type } = storeToRefs(popup);

type AchievementType =
  | "thegetaway"
  | "firewall"
  | "crackthesafe"
  | "puzzle"
  | "pipe";

const isLoading = ref(false);
const achievements = ref<GroupedAchievements>({});
const seasonMap: Record<string, string> = {
  "Season 1 - Puzzle": "puzzle",
  "Season 1 - Crack the Safe": "crackthesafe",
  "Season 1 - Pipe": "pipe",
  "Season 1 - Firewall": "firewall",
  "Season 1 - The Getaway": "thegetaway",
};

const loadAchievements = async () => {
  if (Object.keys(achievements.value).length) return;
  isLoading.value = true;

  try {
    const data = await user.getAchievements();
    if (!data) return;

    const grouped = (data as PagedList<IGameAchievementItems>).items
      .sort((a, b) => a.id - b.id)
      .reduce((acc, item) => {
        const key = seasonMap[item.gameSeason.name];
        if (!key) return acc;

        (acc[key] ??= []).push({
          id: item.id,
          name: item.name,
          text: item.description,
        });

        return acc;
      }, {} as GroupedAchievements);

    achievements.value = grouped;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => popup.showPopup,
  async (value) => {
    if (!value) return;
    await loadAchievements();
  },
);

const closePopup = () => {
  popup.showPopup = false;
};

const bgImage = computed(() => {
  return new URL(
    `../assets/img/achievement-bg/${type.value}.png`,
    import.meta.url,
  ).href;
});

import theGetawayLogo from "~/assets/img/the-getaway.png";
import firewallLogo from "~/assets/img/logo-firewall.png";
import safeLogo from "~/assets/img/logo-safe.png";
import puzzleLogo from "~/assets/img/logo-puzzle.png";
import pipeLogo from "~/assets/img/logo-pipe.png";

const achievementImages: Record<
  AchievementType,
  {
    src: string;
    class: string;
  }
> = {
  thegetaway: {
    src: theGetawayLogo,
    class:
      "w-auto max-h-[73px] object-contain mx-auto mb-2 relative left-[-15px] mt-[-9px]",
  },
  firewall: {
    src: firewallLogo,
    class: "h-[50px] object-contain mx-auto mb-5",
  },
  crackthesafe: {
    src: safeLogo,
    class: "h-[35px] object-contain mx-auto mb-[26px] mt-[9px]",
  },
  puzzle: {
    src: puzzleLogo,
    class: "h-[36px] object-contain mx-auto mt-3 mb-[26px]",
  },
  pipe: {
    src: pipeLogo,
    class: "w-auto max-h-[73px] object-contain mx-auto mb-2 relative mt-[-9px]",
  },
};
</script>

<style scoped>
.achievement-item {
  img {
    @apply mx-auto;
  }
  h4 {
    @apply text-[#FF8000] text-[20px] font-bold leading-[120%] my-0.5 text-center;
  }
  p {
    @apply text-[#898B8E] text-[12px] font-normal leading-[114%] text-center;
  }
}

.loader {
  --color-1: #fff;
  --size: 0.6px;

  width: calc(48 * var(--size));
  height: calc(48 * var(--size));
  border-radius: 50%;
  position: relative;
  animation: rotate 1s linear infinite;
  margin: 0 auto;
}
.loader::before {
  content: "";
  box-sizing: border-box;
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: calc(5 * var(--size)) solid var(--color-1);
  animation: prixClipFix 2s linear infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes prixClipFix {
  0% {
    clip-path: polygon(50% 50%, 0 0, 0 0, 0 0, 0 0, 0 0);
  }
  25% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 0, 100% 0, 100% 0);
  }
  50% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
  }
  75% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 100%);
  }
  100% {
    clip-path: polygon(50% 50%, 0 0, 100% 0, 100% 100%, 0 100%, 0 0);
  }
}
</style>
